import type { MutationPayloadWithErrors, MutationRawResult, MutationValidationResult } from "~/helpers/runMutation";

import classifyRtkError from "~/helpers/rtkErrorClassification";
import unauthorizedRetryPolicy from "~/helpers/unauthorizedRetryPolicy";

export type MutationPayloadErrors = NonNullable<MutationPayloadWithErrors["errors"]>;

export interface MutationStageEvidence<TPayload, TStage extends string> {
	attemptId: string;
	preEffectCodes: readonly string[];
	queuedClientId?: string | null;
	selectIds: (payload: TPayload) => ReadonlyArray<string | null | undefined> | null | undefined;
	stage: TStage;
}

export type MutationStageOutcome<TStage extends string = string> =
	| { attemptId: string; ids: readonly string[]; kind: "acknowledged"; stage: TStage }
	| {
			attemptId: string;
			cause: "notSent" | "preEffectCode";
			errors: MutationPayloadErrors;
			kind: "rejectedBeforeEffect";
			stage: TStage;
	  }
	| {
			attemptId: string;
			errors: MutationPayloadErrors;
			ids: readonly string[];
			kind: "unknown";
			sessionRenewed: boolean;
			stage: TStage;
			transportError: unknown;
	  }
	| { attemptId: string; kind: "queued"; stage: TStage };

export type MutationOutcomeKind = MutationStageOutcome["kind"];

export type MutationStageReceipt<TStage extends string = string> = Extract<
	MutationStageOutcome<TStage>,
	{ kind: "acknowledged" }
>;

export type UnresolvedMutationStage<TStage extends string = string> = Exclude<
	MutationStageOutcome<TStage>,
	{ kind: "acknowledged" }
>;

export interface MutationOutcome<TStage extends string = string> {
	acknowledged: readonly MutationStageReceipt<TStage>[];
	kind: MutationOutcomeKind;
	unresolved: readonly UnresolvedMutationStage<TStage>[];
}

const UNRESOLVED_PRECEDENCE: readonly UnresolvedMutationStage["kind"][] = ["unknown", "queued", "rejectedBeforeEffect"];

const isPresentId = (id: string | null | undefined): id is string => !!id;

const isDeclaredPreEffectRejection = (errors: MutationPayloadErrors, preEffectCodes: readonly string[]): boolean =>
	errors.length > 0 && errors.every(({ code }) => !!code && preEffectCodes.includes(code));

const classifyStage = <TPayload extends MutationPayloadWithErrors, TStage extends string>(
	payload: TPayload | null | undefined,
	transportError: unknown,
	{ attemptId, preEffectCodes, queuedClientId, selectIds, stage }: MutationStageEvidence<TPayload, TStage>
): MutationStageOutcome<TStage> => {
	const errors = payload?.errors ?? [];
	const selectedIds = (payload ? selectIds(payload) : null) ?? [];
	const ids = selectedIds.filter(isPresentId);

	if (transportError && queuedClientId === attemptId) return { attemptId, kind: "queued", stage };
	if (!transportError && isDeclaredPreEffectRejection(errors, preEffectCodes)) {
		return { attemptId, cause: "preEffectCode", errors, kind: "rejectedBeforeEffect", stage };
	}
	if (!transportError && !errors.length && ids.length > 0 && ids.length === selectedIds.length) {
		return { attemptId, ids, kind: "acknowledged", stage };
	}
	return {
		attemptId,
		errors,
		ids,
		kind: "unknown",
		sessionRenewed: unauthorizedRetryPolicy.isMutationNotReplayedError(transportError),
		stage,
		transportError
	};
};

const classifyRawResult = <TData, TPayload extends MutationPayloadWithErrors, TStage extends string>(
	raw: MutationRawResult<TData>,
	selectPayload: (data: TData) => TPayload | null | undefined,
	evidence: MutationStageEvidence<TPayload, TStage>
): MutationStageOutcome<TStage> =>
	classifyStage(raw.data ? selectPayload(raw.data) : null, raw.transportError, evidence);

const classifyValidationResult = <TPayload extends MutationPayloadWithErrors, TEntity, TStage extends string>(
	result: MutationValidationResult<TPayload, TEntity>,
	evidence: MutationStageEvidence<TPayload, TStage>
): MutationStageOutcome<TStage> => classifyStage(result.payload, result.transportError, evidence);

const notSent = <TStage extends string>(stage: TStage, attemptId: string): MutationStageOutcome<TStage> => ({
	attemptId,
	cause: "notSent",
	errors: [],
	kind: "rejectedBeforeEffect",
	stage
});

const errorMessageOf = (outcome: MutationStageOutcome | undefined): string =>
	(outcome && "errors" in outcome ? outcome.errors[0]?.message : undefined) ?? "";

const foldStages = <TStage extends string>(
	stages: readonly MutationStageOutcome<TStage>[]
): MutationOutcome<TStage> => {
	const acknowledged = stages.filter((stage): stage is MutationStageReceipt<TStage> => stage.kind === "acknowledged");
	const unresolved = stages.filter((stage): stage is UnresolvedMutationStage<TStage> => stage.kind !== "acknowledged");
	const kind = UNRESOLVED_PRECEDENCE.find((pending) => unresolved.some((stage) => stage.kind === pending));
	return { acknowledged, kind: kind ?? "acknowledged", unresolved };
};

export interface MutationStagePlanEntry<TStage extends string = string> {
	send: () => Promise<MutationStageOutcome<TStage>>;
	stage: TStage;
}

const runStages = async <TStage extends string>(
	plan: readonly MutationStagePlanEntry<TStage>[],
	attemptId: string
): Promise<MutationOutcome<TStage>> => {
	const stages: MutationStageOutcome<TStage>[] = [];
	for (const { send, stage } of plan) {
		if (stages.some(({ kind }) => kind !== "acknowledged")) {
			stages.push(notSent(stage, attemptId));
		} else {
			stages.push(await send());
		}
	}
	return foldStages(stages);
};

const isRefusedBeforeWrite = (payloadErrors: MutationPayloadWithErrors["errors"], transportError: unknown): boolean => {
	const transportClass = transportError ? classifyRtkError(transportError) : null;
	return (
		Boolean(payloadErrors?.length) ||
		transportClass === "auth" ||
		transportClass === "client" ||
		unauthorizedRetryPolicy.isMutationNotReplayedError(transportError)
	);
};

const mutationOutcome = {
	classifyRawResult,
	classifyValidationResult,
	errorMessageOf,
	foldStages,
	isRefusedBeforeWrite,
	notSent,
	runStages
};

export default mutationOutcome;
